import React, { useContext, useEffect } from 'react';
import DialogTitle from '@mui/material/DialogTitle';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputLabel from '@mui/material/InputLabel';
import { FormHelperText } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import MenuItem from '@mui/material/MenuItem/MenuItem';
import Drawer from '@mui/material/Drawer';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { createGlobalStyle } from 'styled-components';
import SegmentedButton from '../../SegmentedButton';

import {
  BodyForm,
  CloseButton,
  Content,
  DateField,
  DatePickerGlobalStyles,
  Field,
  Footer,
  FormBox,
  Header,
  Label,
  NewTaskContainer,
  SegmentedDiv,
  StyledSaveButton,
} from './styles';
import GuestBox, { GuestUser } from '../../guestsBox';
import { Task } from '../../../pages/TodoPage';
import { JoyrideContext } from '../../../pages/TodoPage/joyride.context';
import {
  TaskFormCompany,
  TaskFormUsergroup,
  useTaskForm,
} from '../../../hooks/useTaskForm';

interface Props {
  taskToEdit?: Task;
  setTaskToEdit: any;
  getAllTasks?: Function;
  taskList: Task[];
  setTaskList: Function;
}

const GlobalStyle = createGlobalStyle<{ disableOverflow: boolean }>`
  body {
    overflow: ${props => (props.disableOverflow ? 'hidden' : 'unset')};
  }
`;

const TaskBar: React.FC<Props> = ({
  taskToEdit = undefined,
  setTaskToEdit,
  setTaskList,
}) => {
  const {
    task,
    required,
    companiesList,
    usergroupsList,
    filteredUserList,
    hideCompany,
    renderValueCompany,
    renderValueUsergroup,
    renderValueAssign,
    handleChange,
    handleChangeAssign,
    handleChangeCompany,
    handleChangeUsergroup,
    reset,
    submit,
  } = useTaskForm(taskToEdit);

  const ItemHeight = 48;
  const ItemPaddingTop = 8;
  const MenuProps = {
    PaperProps: {
      style: { maxHeight: ItemHeight * 4.5 + ItemPaddingTop, width: 250 },
    },
  };

  const isEditingTask = taskToEdit?.shortTitle;
  const showTaskBar = !!(taskToEdit && taskToEdit !== undefined);

  useEffect(() => {
    reset(taskToEdit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskToEdit]);

  const handleChangeEffort = (value: string): void => {
    handleChange('effort', value);
  };

  const handleChangeImpact = (value: string): void => {
    handleChange('impact', value);
  };

  const handleChangeStatus = (value: any): void => {
    handleChange('status', value);
  };

  const handleChangeGuests = (value: GuestUser[]): void => {
    handleChange('guests', value);
  };

  const handleChangeCompanyEvent = (event: SelectChangeEvent<any>): void => {
    handleChangeCompany(event.target.value);
  };

  const handleChangeUsergroupEvent = (event: SelectChangeEvent<any>): void => {
    handleChangeUsergroup(event.target.value);
  };

  const closeNewTaskPanel = (): void => {
    setTaskToEdit(undefined);
    reset(undefined);
  };

  const addTask = (): void => {
    submit().then(savedTask => {
      if (!savedTask) return;

      if (isEditingTask) {
        setTaskList((prev: Task[]) =>
          prev.map((t: Task) =>
            t.taskId === savedTask.taskId ? savedTask : t,
          ),
        );
        setTaskToEdit(savedTask);
      } else {
        setTaskList((prev: Task[]) => [...prev, savedTask]);
      }
      closeNewTaskPanel();
    });
  };

  const joyrideContext = useContext(JoyrideContext);

  return (
    <>
      {showTaskBar && <GlobalStyle disableOverflow={showTaskBar} />}

      <Drawer
        anchor="right"
        open={showTaskBar}
        onClose={closeNewTaskPanel}
        PaperProps={{
          sx: {
            width: 360,
          },
        }}
      >
        <NewTaskContainer id="task-bar">
          <Content id="content">
            <Header id="header">
              <DialogTitle sx={{ padding: 0, color: 'black !important' }}>
                {' '}
                {/* //TODO: COLOR ACCORDING WITH THEME */}
                {isEditingTask
                  ? 'Aufgabe bearbeiten'
                  : 'Neue Aufgabe hinzufügen'}
              </DialogTitle>
              <CloseButton
                ref={joyrideContext?.closeNewTaskDrawerButtonRef}
                title={'Close'}
                variant="contained"
                onClick={() => {
                  setTaskToEdit(undefined);
                }}
              >
                X
              </CloseButton>
            </Header>

            <BodyForm id="body-form">
              <Field
                margin="dense"
                id="taskTitle"
                name="taskTitle"
                value={task?.shortTitle ?? ''}
                onChange={e => handleChange('shortTitle', e.target.value)}
                label="Kurztitel"
                type="text"
                variant="outlined"
                error={!!required.shortTitle}
                helperText={required.shortTitle ? 'required' : ''}
                inputProps={{ maxLength: 20 }}
                // slotProps={{ htmlInput: { maxLength: 12 } }}
              />

              <Field
                margin="dense"
                id="taskDescription"
                name="taskDescription"
                value={task?.description ?? ''}
                onChange={e => handleChange('description', e.target.value)}
                label="Thema"
                type="text"
                variant="outlined"
                error={!!required.description}
                helperText={required.description ? 'required' : ''}
                inputProps={{ maxLength: 5000 }}
              />

              <Field
                margin="dense"
                id="taskGoal"
                name="taskGoal"
                value={task?.goal ?? ''}
                onChange={e => handleChange('goal', e.target.value)}
                label="Ziel"
                type="text"
                variant="outlined"
                inputProps={{ maxLength: 200 }}
              />

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DemoContainer components={['DatePicker']}>
                  <DatePickerGlobalStyles />
                  <DateField
                    label="Fällig"
                    value={dayjs(task?.due || new Date())}
                    onChange={newValue =>
                      handleChange('due', newValue?.toDate() ?? new Date())
                    }
                  />
                </DemoContainer>
              </LocalizationProvider>

              {!hideCompany && (
                <>
                  <FormBox error={!!required.companyId}>
                    <InputLabel id="company-select-label">Company</InputLabel>
                    <Select
                      // disabled={hideCompany}
                      labelId="company-select-label"
                      id="company-select"
                      name="companyList"
                      label="companyList"
                      value={
                        Number(task?.companyId) ? Number(task?.companyId) : ''
                      }
                      onChange={handleChangeCompanyEvent}
                      input={<OutlinedInput label="Company" />}
                      renderValue={() => renderValueCompany}
                      MenuProps={MenuProps}
                    >
                      {companiesList?.map(
                        (company: TaskFormCompany, index: number) => (
                          <MenuItem
                            key={index}
                            value={Number(company.companyId)}
                          >
                            {company.name}
                          </MenuItem>
                        ),
                      )}
                    </Select>
                    <FormHelperText>
                      {required.company ? 'required' : ''}
                    </FormHelperText>
                  </FormBox>
                </>
              )}

              <FormBox error={!!required.userGroups}>
                <InputLabel id="usergroup-label">
                  Organisationseinheit
                </InputLabel>
                <Select
                  labelId="usergroup-label"
                  id="usergroup-select"
                  name="usergroupList"
                  disabled={task === undefined || task?.companyId === undefined}
                  value={
                    Number(task?.usergroupId) ? Number(task?.usergroupId) : ''
                  }
                  onChange={handleChangeUsergroupEvent}
                  input={<OutlinedInput label="Organisationseinheit" />}
                  renderValue={() => renderValueUsergroup}
                  MenuProps={MenuProps}
                >
                  {usergroupsList?.map(
                    (usergroup: TaskFormUsergroup, index: number) => (
                      // print the usergroup name

                      <MenuItem
                        key={index}
                        value={Number(usergroup.usergroupId)}
                      >
                        {usergroup.name}
                      </MenuItem>
                    ),
                  )}
                </Select>
                <FormHelperText>
                  {required.usergroups ? 'required' : ''}
                </FormHelperText>
              </FormBox>

              <FormBox error={!!required.assign}>
                <InputLabel id="assign-label">Verantwortlich</InputLabel>
                <Select
                  disabled={
                    task === undefined ||
                    task?.usergroupId === undefined ||
                    task?.usergroupId === 0
                  }
                  labelId="assign-label"
                  id="assign-select"
                  name="taskAssign"
                  value={
                    filteredUserList.length > 0
                      ? (task?.assign?.email ?? '')
                      : ''
                  }
                  onChange={e => handleChangeAssign(e.target.value)}
                  input={<OutlinedInput label="Verantwortlich" />}
                  renderValue={() => renderValueAssign}
                  MenuProps={MenuProps}
                >
                  {filteredUserList?.map((userMock: any, index: number) => (
                    <MenuItem key={index} value={userMock.email}>
                      {userMock.name}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {required.assign ? 'required' : ''}
                </FormHelperText>
              </FormBox>

              <SegmentedDiv>
                <Label htmlFor="taskImpact">Aufwand</Label>
                <SegmentedButton
                  id="taskEffort"
                  options={['Sehr Gering', 'Gering', 'Hoch', 'Sehr Hoch']}
                  value={task?.effort ?? 'Sehr Gering'}
                  onChange={handleChangeEffort}
                />
              </SegmentedDiv>

              <SegmentedDiv>
                <Label htmlFor="taskImpact">Impact</Label>
                <SegmentedButton
                  id="taskImpact"
                  options={['Sehr Gering', 'Gering', 'Hoch', 'Sehr Hoch']}
                  value={task?.impact ?? 'Sehr Gering'}
                  onChange={handleChangeImpact}
                />
              </SegmentedDiv>

              <SegmentedDiv>
                <Label htmlFor="taskStatus">Status</Label>
                <SegmentedButton
                  id="taskStatus"
                  options={['Nicht Begonnen', 'Laufend', 'Erledigt']}
                  renderOptions={[
                    { label: '🕒 Nicht Begonnen', value: 'Nicht Begonnen' },
                    { label: '🔄 Laufend', value: 'Laufend' },
                    { label: '✔️ Erledigt', value: 'Erledigt' },
                  ]}
                  value={task?.status ?? 'Nicht Begonnen'}
                  onChange={handleChangeStatus}
                />
              </SegmentedDiv>

              <GuestBox
                initialGuests={task?.guests?.map((x: GuestUser) => {
                  return { name: x.name, lastName: x.lastName, email: x.email };
                })}
                handleChangeGuests={handleChangeGuests}
              />
            </BodyForm>

            <Footer id="footer">
              <StyledSaveButton onClick={addTask}>OK</StyledSaveButton>
            </Footer>
          </Content>
        </NewTaskContainer>
      </Drawer>
    </>
  );
};

export default TaskBar;
